/**
 * Applicant history — `/underwriting/applicant_history/*`
 * (frontend_documentation/applicant-history.md, eastview-os
 * `app/routers/applicant_history.py`).
 *
 * For each borrower on a loan: that person's OTHER EV + LQ loans (closed and
 * Salesforce-only included) with live FCI servicing status. Information only —
 * it raises no conditions and blocks nothing.
 *
 * The router lives under `/underwriting` ONLY, but `history_for_deal` resolves
 * the deal in either collection — so this is called by its literal path on
 * both pipelines, never through the pipeline `apiPrefix` (which would 404 on
 * `/loan_acquisitions`).
 *
 * Timing: the backend refreshes a stored result ~30 s after borrowers change,
 * so a GET is usually instant (`cached: true`). An uncached GET downloads
 * FCI's whole book and can take ~25 s — hence the long timeout.
 *
 * Privacy: SSN / date of birth are used for matching only and never come back.
 */
import { losHttp } from "../lib/losHttp";

const PREFIX = "/underwriting/applicant_history";
const TIMEOUT_MS = 90 * 1000;

export const applicantHistoryApi = {
  /**
   * GET /underwriting/applicant_history/{dealId}[?refresh=true]
   * → `{ dealId, loanId, pipeline, generatedAt, cached, trigger, currentLos,
   *      searched{los[], salesforce, fci}, fciPull, notes[], applicants[] }`
   */
  forDeal: (dealId, { refresh = false } = {}) =>
    losHttp
      .get(`${PREFIX}/${dealId}`, {
        params: refresh ? { refresh: true } : undefined,
        timeout: TIMEOUT_MS,
      })
      .then((r) => r.data),

  /**
   * POST /underwriting/applicant_history/lookup — one unsaved person.
   * Body: `{ firstName, lastName, borrowerSSN, dateOfBirth, emailAddress,
   * excludeDealId }`; needs a last name, SSN or email (400 otherwise).
   * → `{ applicants: [one], currentLos, searched, fciPull, notes }`. Nothing stored.
   */
  lookup: (person) =>
    losHttp
      .post(`${PREFIX}/lookup`, person, { timeout: TIMEOUT_MS })
      .then((r) => r.data),
};

export default applicantHistoryApi;
